import type { SoccerGameState } from '../../picksheet-game.interface';
import type { WeatherIconType } from '../../player-pool-member.interface';

export type GameStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'FINISHED' | 'FINALIZED' | 'CANCELED';

export interface TeamInjury {
  position: string;
  name: string;
  primary: string;
  status: string;
  abbrStatus: string;
  fullStatus: string;
  secondary?: string;
  playerId: string;
}

export interface TeamState {
  id: string;
  points?: number;
  runs?: number;
  alias: string;
  primary_color?: string;
  team_color?: string;
  icon_url?: string;
  injuries?: TeamInjury[];
  lineup?: {
    id: string;
    inning: number;
    order?: number;
  }[];
  record?: {
    wins: number;
    losses: number;
    ties?: number;
  };
}

export interface FourQuarterState {
  league: 'nba' | 'nfl' | 'ncaaf';
  clock: string;
  quarter: number;
  phase: 'overtime' | 'regulation' | 'pregame';
}

export type HockeyWithClockState = {
  league: 'nhl';
  clock?: string;
  period: number;
  phase: 'overtime' | 'regulation' | 'pregame';
};

export type HockeyShootoutState = {
  league: 'nhl';
  period: number;
  clock?: string;
  phase: 'shootout';
};

export interface TwoHalvesState {
  league: 'ncaab';
  clock: string;
  half: number;
  phase: 'overtime' | 'regulation' | 'pregame';
}

export interface BaseballState {
  league: 'mlb';
  current_inning_phase: 'top' | 'bottom';
  current_inning?: number;
  outs?: number;
  weather_icon?: WeatherIconType;
}

export type PGAState = {
  league: 'pga';
  rounds: {
    number: number;
    status: GameStatus;
  }[];
};

export type SoccerState = {
  sport?: 'soccer';
  type: 'soccer';
  league: 'wc' | 'epl';
  home_score: number;
  away_score: number;
  match_status: SoccerGameState['matchStatus'];
  clock?: string;
  home_penalties?: number;
  away_penalties?: number;
};

export type PublicNBAGame = {
  id: string;
  name: string;
  league: 'nba';
  start_date: number;
  status: GameStatus;
  home: TeamState;
  away: TeamState;
  state: FourQuarterState;
};

export type PublicFootballGame = {
  id: string;
  name: string;
  league: 'nfl' | 'ncaaf';
  start_date: number;
  status: GameStatus;
  home: TeamState;
  away: TeamState;
  state: FourQuarterState;
};

export type PublicCollegeBasketballGame = {
  id: string;
  name: string;
  league: 'ncaab';
  start_date: number;
  status: GameStatus;
  home: TeamState;
  away: TeamState;
  state: TwoHalvesState;
};

export type PublicHockeyGame = {
  id: string;
  name: string;
  league: 'nhl';
  start_date: number;
  status: GameStatus;
  home: TeamState;
  away: TeamState;
  state: HockeyWithClockState | HockeyShootoutState;
};

export type PublicBaseballGame = {
  id: string;
  name: string;
  league: 'mlb';
  start_date: number;
  status: GameStatus;
  home: TeamState;
  away: TeamState;
  state: BaseballState;
};

export type PublicSoccerGame = {
  id: string;
  name: string;
  // Competition-specific league alias. Soccer is identified at the state level
  // via `state.type === 'soccer'`; the league field here narrows to the actual
  // competition. Expand this union as new soccer competitions ship from BE.
  league: 'wc' | 'epl';
  start_date: number;
  status: GameStatus;
  home: TeamState;
  away: TeamState;
  state?: SoccerState;
};

/** PGA tournament (no home/away teams) */
export type PublicPGAGame = {
  id: string;
  name: string;
  league: 'pga';
  start_date: number;
  status: GameStatus;
  state: PGAState;
};

/** Tournament/event-style games with no home/away teams; extend with more leagues as needed */
export type PublicTournamentGame = PublicPGAGame;

/** Team-based game (has home/away); use to narrow from PublicGame */
export type PublicGameWithTeams =
  | PublicNBAGame
  | PublicFootballGame
  | PublicCollegeBasketballGame
  | PublicHockeyGame
  | PublicBaseballGame
  | PublicSoccerGame;

export type PublicGame = PublicGameWithTeams | PublicTournamentGame;

export function isPublicGameWithTeams(game: PublicGame): game is PublicGameWithTeams {
  return 'home' in game && 'away' in game;
}
